import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TabbedView, TabList, Tab } from 'components/shared/TabbedView';
import request from 'utils/request_ajax';
import classNames from 'classnames';
import TextPlaceholder from 'components/shared/TextPlaceholder';
import { useSelector } from 'react-redux';
import NoAnalytics from './svg/NoAnalytics.jsx';
import Topbar from './Topbar';
import ReportingTable from './ReportingTable';
import JourneyBanner from '../JourneyBuilder/Banner/JourneyBanner';

const Skeleton = () => (
  <div className="JourneyReporting-content">
    <div className="JourneyReporting-contentHeader">
      <TextPlaceholder large minWidth={30} maxWidth={30} />
    </div>
    <div className="JourneyReporting-contentMain">
      <div className="JourneyReportingLoader-tabbedView">
        <div className="JourneyReportingLoader-tab JourneyReportingLoader-tab--active">
          <TextPlaceholder maxWidth={70} minWidth={70} />
          <TextPlaceholder large maxWidth={20} minWidth={20} />
        </div>
        <div className="JourneyReportingLoader-tab">
          <TextPlaceholder maxWidth={70} minWidth={70} />
          <TextPlaceholder large maxWidth={20} minWidth={20} />
        </div>
      </div>
      <table className="JourneyReportingTable">
        <thead className="JourneyReportingTable-head">
          <tr>
            <th className="JourneyReportingTable-headCell JourneyReportingTable-cell">
              <TextPlaceholder large minWidth={35} maxWidth={35} />
            </th>
            <th className="JourneyReportingTable-headCell JourneyReportingTable-cell">
              <TextPlaceholder large minWidth={50} maxWidth={50} />
            </th>
            <th className="JourneyReportingTable-headCell JourneyReportingTable-cell">
              <TextPlaceholder large minWidth={50} maxWidth={50} />
            </th>
          </tr>
        </thead>
        <tbody>
          {Array.from(Array(9).keys()).map(i => (
            <tr key={i} className="JourneyReportingTable-row">
              <td className="JourneyReportingTable-cell">
                <TextPlaceholder large minWidth={25} maxWidth={70} />
              </td>
              <td className="JourneyReportingTable-cell">
                <TextPlaceholder large minWidth={60} maxWidth={60} />
              </td>
              <td className="JourneyReportingTable-cell">
                <TextPlaceholder large minWidth={60} maxWidth={60} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const Empty = ({ type }) => {
  const getReportingEmptyTextMain = {
    complete: t('journeys.reporting.empty.no_complete_learners'),
    incomplete: t('journeys.reporting.empty.no_incomplete_learners'),
    overdue: t('journeys.reporting.empty.no_overdue_learners'),
  };

  const getReportingEmptyTextBody = {
    complete: t('journeys.reporting.empty.no_complete_learners_paragraph'),
    incomplete: t('journeys.reporting.empty.no_incomplete_learners_paragraph'),
    overdue: t('journeys.reporting.empty.no_overdue_learners_paragraph'),
  };

  return (
    <div className="JourneyReportingEmpty">
      <div className="JourneyReportingEmpty-content">
        <NoAnalytics />
        <div className="JourneyReportingEmpty-textMain">
          {getReportingEmptyTextMain[type]}
        </div>
        <div className="JourneyReportingEmpty-textBody">
          {getReportingEmptyTextBody[type]}
        </div>
      </div>
    </div>
  );
};

const JourneyReporting = () => {
  const { journeyId } = useParams();
  const { message, status } = useSelector(state => state);
  const { reporting, showBanner } = useSelector(state => state.ui);

  const [incompleteToggleIndex, setIncompleteToggleIndex] = useState(0);
  const [reportingData, setReportingData] = useState(null);

  useEffect(() => {
    request
      .get(window.location.pathname + window.location.search)
      .then(response => {
        setReportingData(response);
      })
      .fail(err => {
        console.error(err);
        // TODO
      });
  }, []);

  const handleToggle = index => {
    if (index === 1) {
      window.location.assign(`/journeys/${journeyId}`);
    }
  };

  const incompleteActiveClass = classNames('JourneyReporting-counter', {
    'JourneyReporting-counter--active': incompleteToggleIndex === 0,
  });

  const overdueActiveClass = classNames('JourneyReporting-counter', {
    'JourneyReporting-counter--active': incompleteToggleIndex === 1,
  });

  const completeActiveClass = classNames('JourneyReporting-counter', {
    'JourneyReporting-counter--active': incompleteToggleIndex === 2,
  });

  const topbarClassName = classNames('JourneyReporting', {
    'JourneyReporting-topBar--showBanner': showBanner && reporting,
  });

  const reportingTab = index => {
    const types = ['incomplete', 'overdue', 'complete'];

    const getReportingProgresses = [
      reportingData.incompleteProgresses,
      reportingData.overdueProgresses,
      reportingData.completedProgresses,
    ];

    if (getReportingProgresses[index].length === 0) {
      return <Empty type={types[index]} />;
    }

    return (
      <ReportingTable
        data={getReportingProgresses[index]}
        type={types[index]}
      />
    );
  };

  return (
    <>
      {showBanner && <JourneyBanner message={message} state={status} />}
      <div className={topbarClassName}>
        <Topbar />
        <div className="JourneyReporting-main">
          <TabbedView onSelect={index => handleToggle(index)} selectedIndex={0}>
            <TabList>
              <Tab>{t('journeys.reporting.reporting')}</Tab>
              <Tab>{t('journeys.reporting.steps')}</Tab>
            </TabList>
          </TabbedView>
          {reportingData === null && <Skeleton />}
          {reportingData && reportingData.incompleteProgresses && (
            <div className="JourneyReporting-content">
              <div className="JourneyReporting-contentHeader">
                {t('journeys.reporting.learner_progress')}
              </div>
              <div className="JourneyReporting-contentMain">
                <TabbedView
                  onSelect={index => setIncompleteToggleIndex(index)}
                  selectedIndex={incompleteToggleIndex}
                >
                  <TabList>
                    <Tab>
                      <div className="JourneyReporting-tabContent">
                        {t('journeys.reporting.incomplete')}
                        <div className={incompleteActiveClass}>
                          {reportingData.incompleteProgresses.length}
                        </div>
                      </div>
                    </Tab>
                    <Tab>
                      <div className="JourneyReporting-tabContent">
                        {t('journeys.reporting.overdue')}
                        <div className={overdueActiveClass}>
                          {reportingData.overdueProgresses.length}
                        </div>
                      </div>
                    </Tab>
                    <Tab>
                      <div className="JourneyReporting-tabContent">
                        {t('journeys.reporting.complete')}
                        <div className={completeActiveClass}>
                          {reportingData.completedProgresses.length}
                        </div>
                      </div>
                    </Tab>
                  </TabList>
                </TabbedView>
                {reportingTab(incompleteToggleIndex)}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default JourneyReporting;
